<template>
  <div>
    <v-dialog v-model="show" max-width="850px" :fullscreen="isMobile">
      <v-card class="late-checkin-dialog" tile :loading="isFetchingAvailableCheckinDates || isFetchingAvailableCheckoutDates">
        <v-toolbar elevation="0">
          <v-toolbar-title class="">
            {{ isLateCheckout ? 'Sen incheckning och utcheckning' : 'Sen incheckning' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn v-if="isMobile" @click="close" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider></v-divider>

        <v-sheet
          class="d-flex overflow-y-auto pa-4"
          :class="{ 'flex-column': isMobile }"
        >
          <div class="d-flex mr-2">
            <v-card outlined class="flex-shrink-1 flex-grow-0 mx-auto">
              <v-card-title class="primary white--text">
                Välj {{ selectingDateType === 'checkin' ? 'incheckningsdatum' : 'utcheckningsdatum' }}
              </v-card-title>
              <v-date-picker
                v-model="datepickerInput"
                :allowed-dates="isAvailableDate"
                color="primary"
                :first-day-of-week="1"
                no-title
                :picker-date="displayedCalendarMonth"
              ></v-date-picker>
            </v-card>
          </div>
          <div class="ml-2 d-flex flex-grow-1 flex-column">
            <template v-if="!!errorMessage">
              <div class="pa-4">
                <v-alert
                  type="error"
                  text
                  class=""
                >
                  {{ errorMessage }}
                </v-alert>
              </div>
            </template>
            <template v-else-if="isFetchingAvailableCheckinDates">
              <div class="pa-4 d-flex full-height align-center full-width justify-center">
                <v-progress-circular
                  indeterminate
                  size="48"
                  color="primary"
                ></v-progress-circular>
              </div>
            </template>
            <template v-else>
              <div class="pa-4">
                <v-alert
                  type="warning"
                  text
                  class="alert-center-icon"
                >
                  Bokningen skulle checkat in {{ checkinDateFormatted }}
                </v-alert>
                <v-select
                  v-model="selectedCheckinDate"
                  :items="availableCheckinDateItems"
                  label="Välj bokningens verkliga incheckningsdatum"
                  outlined
                  hide-details
                ></v-select>
              </div>

              <template v-if="isLateCheckout">
                <div class="p-relative pa-4">
                  <div class="p-absolute t-0 l-0 full-width full-height z-2 rounded pointer-events-none" :class="{ 'opacity-50 white': !selectedCheckinDate }"></div>
                  <v-alert
                    type="warning"
                    text
                    class="alert-center-icon mt-4"
                  >
                    Bokningen skulle checkat ut {{ checkoutDateFormatted }}
                  </v-alert>
                  <v-select
                    v-model="selectedCheckoutDate"
                    :loading="isFetchingAvailableCheckoutDates"
                    :items="availableCheckoutDateItems"
                    :disabled="!selectedCheckinDate"
                    label="Välj bokningens verkliga utcheckningsdatum"
                    outlined
                    hide-details
                  ></v-select>
                </div>
              </template>
            </template>

            <div class="full-width d-flex mt-auto pt-6">
              <v-btn
                @click="saveDates"
                :disabled="!datepickerInput || isFetchingAvailableCheckinDates || isFetchingAvailableCheckoutDates"
                :loading="isSavingDates"
                class="mt-auto ml-auto"
                color="success"
                :block="isMobile"
                :x-large="isMobile"
              >
                {{ saveButtonText }}
              </v-btn>
            </div>
          </div>
        </v-sheet>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import BookingService from '@/services/BookingService.js'

export default {
  name: 'LateCheckinDialog',
  data: () => ({
    selectingDateType: 'checkin', // 'checkin' eller 'checkout'
    selectedCheckinDate: null,
    selectedCheckoutDate: null,
    isFetchingAvailableCheckinDates: false,
    isFetchingAvailableCheckoutDates: false,
    displayedCalendarMonth: window.dayjs.today().format('YYYY-MM-DD'),
    isSavingDates: false,
    availableCheckinDates: [], // array med YYYY-MM-DD formatterade datumsträngar
    availableCheckoutDates: [] // array med YYYY-MM-DD formatterade datumsträngar
  }),
  computed: {
    ...mapState(
      {
        booking: state => state.booking.lateCheckinDialog.booking
      }
    ),
    show: {
      get () {
        return this.$store.state.booking.lateCheckinDialog.show
      },
      set (value) {
        this.$store.commit('booking/SET_SHOW_LATE_CHECKIN_DIALOG', value)
      }
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    errorMessage: function () {
      if (this.booking && !this.isFetchingAvailableCheckinDates && this.availableCheckinDates.length === 0) {
        return 'Det finns inga datum på den här platsen att checka in bokningen. Detta kan bero på att en incheckad bokning har checkat in på samma plats före denna bokningens incheckningsdatum. Checka ut den bokningen först, alternativt kan du avboka den här bokningen.'
      }
      return null
    },
    datepickerInput: {
      get () {
        if (this.selectingDateType === 'checkin') {
          return this.selectedCheckinDate
        } else {
          return this.selectedCheckoutDate
        }
      },
      set (value) {
        if (this.selectingDateType === 'checkin') {
          this.selectedCheckinDate = value
        } else {
          this.selectedCheckoutDate = value
        }
      }
    },
    isLateCheckout: function () {
      if (!this.booking) {
        return false
      }
      const today = window.dayjs.today()
      return this.booking.check_out.isBefore(today, 'day')
    },
    checkinDateFormatted: function () {
      if (!this.booking) {
        return ''
      }
      return this.booking.check_in.toHumanReadableDate({ longFormat: true, capitalized: false })
    },
    checkoutDateFormatted: function () {
      if (!this.booking) {
        return ''
      }
      return this.booking.check_out.toHumanReadableDate({ longFormat: true, capitalized: false })
    },
    alertMessage: function () {
      if (this.selectingDateType === 'checkin') {
        return `Bokningen skulle checkat in ${this.checkinDateFormatted}`
      } else {
        return `Bokningen skulle checkat ut ${this.checkoutDateFormatted}`
      }
    },
    saveButtonText: function () {
      if (this.isLateCheckout) {
        return 'Checka in och ut'
      } else {
        return 'Checka in'
      }
    },
    availableCheckinDateItems: function () {
      return this.availableCheckinDates.map(date => {
        const dayjsDate = window.dayjs.parseServerDate(date)
        const parenthesisText = matchingDate => {
          if (matchingDate.isSame(this.booking.check_in, 'day')) {
            return ' (bokningens incheckningsdatum)'
          } else if (matchingDate.isSame(this.booking.check_out, 'day')) {
            return ' (bokningens utcheckningsdatum)'
          } else {
            return ''
          }
        }
        return {
          text: dayjsDate.toHumanReadableDate({ longFormat: true, capitalized: true }) + parenthesisText(dayjsDate),
          value: date
        }
      })
    },
    availableCheckoutDateItems: function () {
      const parenthesisText = matchingDate => {
        if (matchingDate.isSame(this.booking.check_in, 'day')) {
          return ' (bokningens incheckningsdatum)'
        } else if (matchingDate.isSame(this.booking.check_out, 'day')) {
          return ' (bokningens utcheckningsdatum)'
        } else {
          return ''
        }
      }
      return this.availableCheckoutDates.map(date => {
        const dayjsDate = window.dayjs.parseServerDate(date)
        return {
          text: dayjsDate.toHumanReadableDate({ longFormat: true, capitalized: true }) + parenthesisText(dayjsDate),
          value: date
        }
      })
    }
  },
  methods: {
    close: function () {
      this.show = false
    },
    saveDates: function () {
      if (!this.selectedCheckinDate || (this.isLateCheckout && !this.selectedCheckoutDate)) {
        // Validerar att datum valts
        return false
      }

      const checkinDate = window.dayjs.parseDate(this.selectedCheckinDate)
      const checkoutDate = window.dayjs.parseDate(this.selectedCheckoutDate)

      if (this.isLateCheckout) {
        // om utcheckningsdatum också passerat så checkar vi in och ut samtidigt
        this.isSavingDates = true
        BookingService.checkinAndCheckout({
          bookingId: this.booking.id,
          checkinDate,
          checkoutDate
        })
          .then(({ data }) => {
            if (data.status === 'success') {
              // Funderade på att routa till kassan men kan bli väldigt förvirrande att komma till kassan och bokningen redan är utcheckad så där är ingen checka ut knapp och inget jättetydligt sätt att se att bokningen redan är utcheckad. Är nog bättre att öppna booking-dialog och det skickas snackbar från server ifall det finns något kvar att betala
              this.close()
              this.$store.dispatch('booking/openDialogById', { bookingId: this.booking.id })
            }
          })
          .finally(() => {
            this.isSavingDates = false
          })
      } else {
        // om endast sen incheckning, öppna incheckningsdialog och checka in med vanliga metoden incheckningsmetoden fast med valt incheckningsdatum
        this.close()
        this.$store.dispatch('booking/checkIn', { booking: this.booking, checkinDate })
      }
    },
    getAvailableCheckinDates: function () {
      const bookingId = this.booking.id
      this.isFetchingAvailableCheckinDates = true
      BookingService.getAvailableCheckinDates({ bookingId })
        .then(({ data }) => {
          this.availableCheckinDates = data.data.available_checkin_dates
          if (this.selectedCheckinDate && !this.availableCheckinDates.includes(this.selectedCheckinDate)) {
            this.selectedCheckinDate = null
          }
          if (this.availableCheckinDates.length > 0) {
            this.displayedCalendarMonth = this.availableCheckinDates[0]
          }
        })
        .finally(() => {
          this.isFetchingAvailableCheckinDates = false
        })
    },
    getAvailableCheckoutDates: function () {
      const bookingId = this.booking.id
      const selectedCheckinDate = window.dayjs.parseDate(this.selectedCheckinDate)
      this.isFetchingAvailableCheckoutDates = true
      BookingService.getAvailableCheckoutDates({ bookingId, selectedCheckinDate })
        .then(({ data }) => {
          this.availableCheckoutDates = data.data.available_checkout_dates
          if (this.selectedCheckoutDate && !this.availableCheckoutDates.includes(this.selectedCheckoutDate)) {
            this.selectedCheckoutDate = null
          }
          if (this.availableCheckoutDates.length > 0) {
            this.displayedCalendarMonth = this.availableCheckoutDates[0]
          }
        })
        .finally(() => {
          this.isFetchingAvailableCheckoutDates = false
        })
    },
    isAvailableDate: function (date) {
      // date är en sträng i formatet YYYY-MM-DD
      if (this.selectingDateType === 'checkin') {
        return this.availableCheckinDates.includes(date)
      } else {
        return this.availableCheckoutDates.includes(date)
      }
    }
  },
  watch: {
    selectedCheckinDate: function () {
      if (!!this.selectedCheckinDate && this.isLateCheckout) {
        // Valt incheckningsdatum och även utcheckningsdatumet har passerat, så hämtar möjliga utcheckningsdatum från server
        this.selectingDateType = 'checkout'
        this.getAvailableCheckoutDates()
      }
    },
    show: function () {
      if (this.show) {
        // Hämtar alltid tillgängliga incheckningsdatum från server, även om utcheckningsdatum inte passerat. Säkerställer att datum som väljs aldrig överlappar med några tenancies. Skulle kunna göra det annars även om det inte är dubbelbokat om bokningen bytt plats
        this.getAvailableCheckinDates()
      } else {
        // Återställer dialog när den stängs
        this.availableCheckinDates = []
        this.availableCheckoutDates = []
        this.selectingDateType = 'checkin'
        this.selectedCheckinDate = null
        this.selectedCheckoutDate = null
        this.isSavingDates = false
        this.isFetchingAvailableCheckinDates = false
        this.isFetchingAvailableCheckoutDates = false
      }
    }
  }
}
</script>
